import { Promotion, Screen, LayoutStyle } from '../../../shared/types';

/**
 * Exporta dados para um arquivo JSON que será baixado pelo usuário
 * @param data Dados a serem exportados
 * @param fileName Nome do arquivo a ser baixado
 */
export const exportToJson = <T>(data: T, fileName: string): void => {
  // Converte os dados para string JSON formatada
  const jsonString = JSON.stringify(data, null, 2);

  // Cria um blob com os dados
  const blob = new Blob([jsonString], { type: 'application/json' });

  // Cria URL para o blob
  const url = URL.createObjectURL(blob);

  // Cria um elemento de link para download
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;

  // Anexa o link ao documento, clica nele e depois remove
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Libera a URL criada
  URL.revokeObjectURL(url);
};

/**
 * Interface para os diferentes tipos de dados que podem ser importados
 */
export interface ImportDataResult {
  promotions?: Promotion[];
  screens?: Screen[];
}

/**
 * Importa dados de um arquivo JSON selecionado pelo usuário
 * @param file Arquivo a ser importado
 * @param onSuccess Callback chamado quando a importação é bem-sucedida
 * @param onError Callback chamado quando ocorre um erro na importação
 */
export const importFromJson = (
  file: File,
  onSuccess: (data: any) => void,
  onError: (error: string) => void
): void => {
  const reader = new FileReader();

  reader.onload = (event) => {
    try {
      if (event.target?.result) {
        const data = JSON.parse(event.target.result as string);
        onSuccess(data);
      } else {
        onError('Não foi possível ler o arquivo.');
      }
    } catch (error) {
      onError(`Erro ao processar o arquivo: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
    }
  };

  reader.onerror = () => {
    onError('Erro ao ler o arquivo.');
  };

  reader.readAsText(file);
};

/**
 * Validação básica dos dados importados
 * @param data Dados importados
 * @param expectedType Tipo esperado dos dados
 */
export const validateImportedData = (data: any, expectedType: 'promotions' | 'screens'): string | null => {
  if (!data || typeof data !== 'object') {
    return 'Formato de dados inválido.';
  }

  if (expectedType === 'promotions') {
    if (!Array.isArray(data)) {
      return 'Os dados importados não são uma lista de promoções.';
    }

    for (const item of data) {
      if (!item.id || !item.name || typeof item.price !== 'number' || !item.unit || !item.validUntil) {
        return 'Uma ou mais promoções estão faltando campos obrigatórios.';
      }
    }
  }

  if (expectedType === 'screens') {
    if (!Array.isArray(data)) {
      return 'Os dados importados não são uma lista de telas.';
    }

    for (const item of data) {
      if (!item.id || !item.name || !item.layoutId || !Array.isArray(item.promotions)) {
        return 'Uma ou mais telas estão faltando campos obrigatórios.';
      }
    }
  }

  return null;
}; 